/**
 * Button Component
 * 
 * Reusable button component with multiple variants:
 * - Primary: Main CTA buttons (indigo/purple)
 * - Secondary: Back buttons, secondary actions
 * - Ghost: Minimal style for tertiary actions
 * 
 * Features:
 * - Loading state with spinner
 * - Disabled state styling
 * - Full width option for mobile
 * - Icon support (left or right)
 */

import React, { forwardRef, ButtonHTMLAttributes } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';

// ============================================
// TYPES
// ============================================

type ButtonVariant = 'primary' | 'secondary' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onAnimationStart' | 'onDrag' | 'onDragEnd' | 'onDragStart'> {
  /** Button style variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Show loading spinner */
  isLoading?: boolean;
  /** Take full width of container */
  fullWidth?: boolean;
  /** Icon to show on left side */
  leftIcon?: React.ReactNode;
  /** Icon to show on right side */
  rightIcon?: React.ReactNode;
  /** Children content */
  children: React.ReactNode;
}

// ============================================
// STYLES
// ============================================

/**
 * Base styles applied to all buttons
 */
const baseStyles = `
  inline-flex items-center justify-center
  font-semibold rounded-xl
  transition-all duration-200
  focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
  disabled:opacity-50 disabled:cursor-not-allowed
`;

/**
 * Variant-specific styles
 */
const variantStyles: Record<ButtonVariant, string> = {
  primary: `
    bg-primary text-white
    hover:bg-primary-600
    focus-visible:ring-primary
    shadow-button hover:shadow-button-hover
  `,
  secondary: `
    bg-white text-text-primary
    border-2 border-border
    hover:border-primary hover:bg-primary/5
    focus-visible:ring-primary
  `,
  ghost: `
    text-text-secondary
    hover:text-text-primary hover:bg-surface
    focus-visible:ring-gray-300
  `,
};

/**
 * Size-specific styles
 */
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-10 px-4 text-sm gap-2',
  md: 'h-14 px-6 text-base gap-2',
  lg: 'h-16 px-8 text-lg gap-3',
};

// ============================================
// LOADING SPINNER
// ============================================

/**
 * Simple loading spinner SVG
 */
const LoadingSpinner = () => (
  <svg
    className="animate-spin h-5 w-5"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

// ============================================
// COMPONENT
// ============================================

/**
 * Button component with Framer Motion animations
 * Uses forwardRef to allow ref passing from parent components
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      children,
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    // Combine all styles
    const combinedStyles = `
      ${baseStyles}
      ${variantStyles[variant]}
      ${sizeStyles[size]}
      ${fullWidth ? 'w-full' : ''}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    // Animation variants for hover/tap
    const motionProps: HTMLMotionProps<'button'> = {
      whileHover: disabled || isLoading ? {} : { scale: 1.02, y: -2 },
      whileTap: disabled || isLoading ? {} : { scale: 0.98 },
      transition: { duration: 0.15 },
    };

    return (
      <motion.button
        ref={ref}
        className={combinedStyles}
        disabled={disabled || isLoading}
        {...motionProps}
        {...(props as HTMLMotionProps<'button'>)}
      >
        {/* Loading spinner or left icon */}
        {isLoading ? (
          <LoadingSpinner />
        ) : leftIcon ? (
          <span className="flex-shrink-0">{leftIcon}</span>
        ) : null}

        {/* Button text */}
        <span>{children}</span>

        {/* Right icon (hidden when loading) */}
        {!isLoading && rightIcon && (
          <span className="flex-shrink-0">{rightIcon}</span>
        )}
      </motion.button>
    );
  }
);

// Display name for debugging
Button.displayName = 'Button';

export default Button;
